import '../../styles/Auth.css';

import {faEye, faEyeSlash} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {createUserWithEmailAndPassword} from 'firebase/auth';
import {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';

import {apiService} from '../../apiService';
import {auth} from '../../firebase';

const Signup = () => {
  const [organizationCode, setOrganizationCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Auto-capitalize each word in a name
  const capitalizeName = (name) =>
    name.replace(/\b[a-z]/g, (char) => char.toUpperCase());

  // Validation functions
  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const validateName = (name) => {
    const regex = /^[A-Za-z\s]+$/;
    return regex.test(name);
  };

  const validatePhone = (phone) => {
    // Allow formats like: 1234567890, 123-456-7890, (123) 456-7890, etc.
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length === 10;
  };

  const formatPhoneNumber = (phone) => {
    return phone.replace(/\D/g, '');
  };

  const validateOrganizationCode = async (code) => {
    try {
      const data = await apiService.post('/api/verify-org-code/', {
        company_code: code,
      });

      return data.valid;
    } catch (error) {
      console.error('Error verifying org code:', error);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate organization code with API
    const isValidOrgCode = await validateOrganizationCode(organizationCode);
    if (!isValidOrgCode) {
      setError('Invalid organization code. Please contact your administrator.');
      setLoading(false);
      return;
    }

    if (!validateName(firstName)) {
      setError('First name should contain only letters.');
      setLoading(false);
      return;
    }

    if (!validateName(lastName)) {
      setError('Last name should contain only letters.');
      setLoading(false);
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    if (phoneNumber && !validatePhone(phoneNumber)) {
      setError('Phone number must be a valid 10-digit number.');
      setLoading(false);
      return;
    }

    try {
      // Step 1: Create user with Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      // Step 2: Register user in the backend using the register_manager endpoint
      try {
        await apiService.post('/api/register-manager/', {
          comp_code: parseInt(organizationCode), // Convert to integer
          first_name: firstName,
          last_name: lastName,
          email: email,
          phone_number: formatPhoneNumber(phoneNumber),
          uid: user.uid,
        });

        // Successfully signed up and registered in backend
        // Redirect to dashboard
        navigate('/dashboard');
      } catch (backendError) {
        // If backend registration fails, delete the Firebase user to maintain consistency
        try {
          await user.delete();
        } catch (deleteError) {
          console.error(
            'Failed to delete Firebase user after backend registration failure:',
            deleteError,
          );
        }

        setError(
          'Registration failed: ' +
            (backendError.response?.data?.error || backendError.message),
        );
        console.error('Backend registration error:', backendError);
      }
    } catch (firebaseError) {
      setError(getErrorMessage(firebaseError.code));
      console.error('Firebase signup error:', firebaseError);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get user-friendly error messages
  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/operation-not-allowed':
        return 'Email/password accounts are not enabled.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection.';
      default:
        return 'An error occurred during signup. Please try again.';
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className='auth-container'>
      <div className='auth-card'>
        <h2>Sign Up to Tenvos</h2>

        {error && <div className='auth-error'>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className='form-group'>
            <label htmlFor='organization-code'>Organization Code</label>
            <input
              type='text'
              id='organization-code'
              value={organizationCode}
              onChange={(e) => setOrganizationCode(e.target.value)}
              required
              disabled={loading}
              placeholder='Enter code provided by administrator'
            />
          </div>

          <div className='form-group'>
            <label htmlFor='first-name'>First Name</label>
            <input
              type='text'
              id='first-name'
              value={firstName}
              onChange={(e) => setFirstName(capitalizeName(e.target.value))}
              required
              disabled={loading}
              placeholder='Enter your first name'
            />
          </div>

          <div className='form-group'>
            <label htmlFor='last-name'>Last Name</label>
            <input
              type='text'
              id='last-name'
              value={lastName}
              onChange={(e) => setLastName(capitalizeName(e.target.value))}
              required
              disabled={loading}
              placeholder='Enter your last name'
            />
          </div>

          <div className='form-group'>
            <label htmlFor='email'>Email</label>
            <input
              type='email'
              id='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder='Enter your email address'
            />
          </div>

          <div className='form-group'>
            <label htmlFor='phone'>Phone Number</label>
            <input
              type='tel'
              id='phone'
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              disabled={loading}
              placeholder='Enter your 10-digit phone number'
            />
          </div>

          <div className='form-group'>
            <label htmlFor='password'>Password</label>
            <div className='password-input-container'>
              <input
                type={showPassword ? 'text' : 'password'}
                id='password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder='Create a password (min. 6 characters)'
              />
              <button
                type='button'
                className='password-toggle-btn'
                onClick={togglePasswordVisibility}
                tabIndex='-1'
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
          </div>

          <button type='submit' className='auth-button' disabled={loading}>
            {loading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        <div className='auth-links'>
          <Link to='/login' className='auth-link'>
            Already have an account? Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
